package com.milta.feature.route.domain.entity

/** MIL-45 — mirrors backend `GeocodeStatus`. [Degraded] means the geocoding service itself was
 * unreachable (retry later), distinct from [Failed] (address genuinely not found). Both render the
 * same "not located" warning to the Gestionnaire — the distinction only matters for retry logic. */
enum class GeocodeStatus {
    Pending,
    Geocoded,
    Failed,
    Degraded,
}

/** Maps the backend's `GeocodeStatus.ToString()` wire value — unrecognized/missing falls back to
 * [GeocodeStatus.Pending], same convention as [com.milta.feature.route.data.remote.dto.DeliveryDto]. */
fun String?.toGeocodeStatus(): GeocodeStatus = when (this) {
    "Geocoded" -> GeocodeStatus.Geocoded
    "Failed" -> GeocodeStatus.Failed
    "Degraded" -> GeocodeStatus.Degraded
    else -> GeocodeStatus.Pending
}

/** Retour 26/10 (demande 2, R1) — why HERE refused an address ([GeocodeStatus.Failed] only): a
 * street found without its house number, or nothing usable found. Mirrors the backend
 * `GeocodeFailureReason` wire value (`geocodeFailureReason`). */
enum class GeocodeFailureReason {
    MissingHouseNumber,
    NotFound,
    /** HERE could not confirm the address: unreachable (wire `ServiceUnavailable` on a Failed status,
     * or the older [GeocodeStatus.Degraded]) or not checked ([GeocodeStatus.Pending], e.g. the
     * validation call failed). Shown as "Adresse non vérifiée", with its retry link. */
    Unverified,
}

/** Unknown/missing wire value → null (the screens then show "Adresse introuvable" for a Failed address). */
fun String?.toGeocodeFailureReason(): GeocodeFailureReason? = when (this) {
    "MissingHouseNumber" -> GeocodeFailureReason.MissingHouseNumber
    "NotFound" -> GeocodeFailureReason.NotFound
    "ServiceUnavailable" -> GeocodeFailureReason.Unverified
    else -> null
}

/** R2 — only an address HERE verified (Geocoded) goes on; anything else blocks the next step with
 * its motif. A Failed address without a reason (back not reporting it yet) counts as not found;
 * Degraded and Pending are "non vérifiée". Null status (not reported by the back) → null. */
fun addressFixReason(status: GeocodeStatus?, reason: GeocodeFailureReason?): GeocodeFailureReason? = when (status) {
    null, GeocodeStatus.Geocoded -> null
    GeocodeStatus.Failed -> reason ?: GeocodeFailureReason.NotFound
    GeocodeStatus.Degraded, GeocodeStatus.Pending -> GeocodeFailureReason.Unverified
}

/** Retour 29/10 — the HERE match to show under a verified address ("Trouvée : …"), only when it
 * differs from the text typed (trimmed, case ignored); null for older data without a match. */
fun foundAddressLabel(status: GeocodeStatus?, typed: String?, resolvedAddress: String?): String? {
    if (status != GeocodeStatus.Geocoded) return null
    val found = resolvedAddress?.trim()?.takeIf { it.isNotEmpty() } ?: return null
    return found.takeUnless { it.equals(typed?.trim().orEmpty(), ignoreCase = true) }
}
